import { useEffect } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { t } from '@/i18n';
import { useStore, type Toast } from '@/state/store';
import { Emoji } from '@/ui';

const TOAST_MS = 4500;

const TONE: Record<Toast['tone'], { cls: string; emoji: string }> = {
  info: { cls: 'bg-white', emoji: '💬' },
  success: { cls: 'bg-jury-tint', emoji: '✅' },
  error: { cls: 'bg-team-b-tint', emoji: '💥' },
};

function ToastItem({ toast }: { toast: Toast }) {
  const dismiss = useStore((s) => s.dismissToast);
  useEffect(() => {
    const id = setTimeout(() => dismiss(toast.id), TOAST_MS);
    return () => clearTimeout(id);
  }, [toast.id, dismiss]);
  const tone = TONE[toast.tone];
  return (
    <motion.li
      layout
      role={toast.tone === 'error' ? 'alert' : 'status'}
      initial={{ x: 120, opacity: 0, rotate: 6 }}
      animate={{ x: 0, opacity: 1, rotate: -1 }}
      exit={{ x: 140, opacity: 0, rotate: 8, transition: { duration: 0.18 } }}
      transition={{ type: 'spring', stiffness: 480, damping: 26 }}
      className={`pointer-events-auto flex items-start gap-3 border-3 border-ink px-3 py-2.5 font-extrabold shadow-ink ${tone.cls}`}
    >
      <Emoji emoji={tone.emoji} label="" size={24} className="mt-0.5 shrink-0" />
      <span className="min-w-0 flex-1 text-[15px] leading-snug">{toast.message}</span>
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={() => dismiss(toast.id)}
        className="comic -mt-0.5 -mr-1 px-1 text-[18px] text-ink/60 hover:text-ink"
      >
        ✕
      </button>
    </motion.li>
  );
}

/** Global toast stack (bottom right). */
export function Toasts() {
  const toasts = useStore((s) => s.toasts);
  return (
    <ol
      aria-label="Bildirimler"
      className="pointer-events-none fixed right-3 bottom-3 z-50 flex w-80 max-w-[90vw] flex-col gap-2"
    >
      <AnimatePresence initial={false}>
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} />
        ))}
      </AnimatePresence>
    </ol>
  );
}
